import {
  BooleanField,
  DateTimeField,
  NumberField,
  RelationshipField,
  RelationshipFieldMany,
  StringField,
  LongTextField,
} from "../plugins/Cruxtify/field-types";
import {
  BooleanProperty,
  DateTimeProperty,
  NumberProperty,
  RelationshipProperty,
  RelationshipPropertyMany,
  StringProperty,
} from "../plugins/Cruxtify/property-types";
import {
  UserPermission,
  UserPermissions,
  UserRole,
} from "../plugins/Cruxtify/UserPermission";
import { ApcCrudModel } from "./ApcCrudModel";

export class Option extends ApcCrudModel {
  public static icon = "mdi-puzzle-piece";
  protected static _typeLabel = "Option";
  protected static asProperty = "option";
  public static api = {
    path: "options",
  };

  protected static routeBase = "options";

  protected static userPermissions = new UserPermissions(
    {
      [UserRole.SuperAdmin]: UserPermission.Edit,
      [UserRole.LoggedIn]: UserPermission.Read,
    },
    UserPermission.New
  );

  protected static propertyDefinitions = [
    {
      type: NumberProperty,
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "created_at",
        label: "Created At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "updated_at",
        label: "Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "deleted_at",
        label: "deleted At",
        userPermissions: UserPermission.Hidden,
        computed: true,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "part_number",
        label: "Part Number",
        sortable: true,
        rules: ["required"],
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "description",
        label: "Description",
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "enable_wp_detail_page",
        label: "List as Parts & Accessories",
      },
    },
    {
      type: BooleanProperty,
      opts: {
        name: "is_baba_compliant",
        label: "Is BABA Compliant",
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "label",
        label: "Label",
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "option_groups",
        label: "Option Groups",
        relatedModel: "OptionGroup",
        foreignProperty: "options",
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "configurable_slots",
        label: "Configurable Slots",
        relatedModel: "ConfigurableSlot",
        foreignProperty: "options",
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "suggested_for_slots",
        label: "Suggested For Slots",
        relatedModel: "ConfigurableSlot",
        foreignProperty: "suggested_option",
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "configurable_slot_type",
        label: "Default Configurable Slot Type",
        relatedModel: "ConfigurableSlotType",
        foreignProperty: "default_for_options",
      },
    },
  ];

  protected static fieldDefinitions = [
    { type: NumberField, opts: { property: "id" } },
    { type: DateTimeField, opts: { property: "created_at" } },
    { type: DateTimeField, opts: { property: "updated_at" } },
    {
      type: StringField,
      opts: { property: "part_number", isPrimaryLabelField: 1 },
    },
    { type: LongTextField, opts: { property: "label" } },
    { type: LongTextField, opts: { property: "description" } },
    { type: BooleanField, opts: { property: "enable_wp_detail_page" } },
    { type: BooleanField, opts: { property: "is_baba_compliant" } },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "option_groups",
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "configurable_slots",
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "suggested_for_slots",
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
      },
    },
    {
      type: RelationshipField,
      opts: {
        property: "configurable_slot_type",
      },
    },
  ];

  protected static collectionLayoutDefinitions = [
    {
      id: "table",
      columnFields: [
        "part_number",
        // "description",
        "configurable_slot_type",
        "enable_wp_detail_page",
      ],
      enableSearch: true,
      enableDynamicFilters: true,
      useModal: true,
      dynamicFilterOptions: {
        filterFields: [
          "part_number",
          // "description",
          "configurable_slot_type",
          "option_groups",
          "enable_wp_detail_page",
          "is_baba_compliant",
          "configurable_slots",
          "created_at",
          "updated_at",
        ],
      },
      defaultSort: [
        {
          field: "part_number",
          order: "asc",
        },
      ],
    },
  ];

  protected static modelLayoutDefinitions = [
    {
      type: "TabbedModelLayout",
      opts: {
        id: "tabs",
        tabs: [
          {
            id: "detailsTab",
            label: "Details",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                id: "optionDetails",
                fieldSets: [
                  {
                    id: "main",
                    fields: [
                      "part_number",
                      "label",
                      "enable_wp_detail_page",
                      "is_baba_compliant",
                      // "description",
                      "configurable_slot_type",
                    ],
                  },
                  {
                    id: "meta",
                    fields: ["created_at", "updated_at", "id"],
                  },
                ],
              },
            },
          },
          {
            id: "relationshipsTab",
            label: "Relationships",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                fieldSets: [
                  {
                    id: "groups",
                    label: "Option Groups",
                    fields: ["option_groups"],
                  },
                  {
                    id: "slots",
                    label: "Configurable Slots",
                    fields: ["configurable_slots", "suggested_for_slots"],
                  },
                ],
              },
            },
          },
        ],
      },
    },
  ];
}
